/**
 * BreakSizeSelects — the three break-size dropdowns (1st / 2nd / 3rd break) of an SOS officer's plan,
 * coloured by length. One component for everywhere an SOS break plan is set (Add SOS, the wizard's SOS
 * table), so the options and colours cannot drift apart.
 */
import { getBreakOptions, getDurationPalette } from "@/config/modeConfig";

const BREAK_OPTIONS = getBreakOptions();

interface BreakSizeSelectsProps {
  /** Break sizes in 15-minute slots, null = none. */
  values: (number | null)[];
  onChange: (index: number, value: number | null) => void;
  /** Resets to the default plan for the timing; the button is shown only when given. */
  onReset?: () => void;
  disabled?: boolean;
}

export const BREAK_LABELS = ["1st break", "2nd break", "3rd break"] as const;

/** One break-size dropdown, coloured by length. */
export function BreakSelect({ value, onChange, label, disabled, emptyLabel = "None", strong }: {
  value: number | null;
  onChange: (value: number | null) => void;
  label: string;
  disabled?: boolean;
  /** What "no break" reads as — "1st" in a row where the position says which break it is. */
  emptyLabel?: string;
  /** A heavier border, so an empty one still reads as a dropdown. */
  strong?: boolean;
}) {
  const palette = value != null ? getDurationPalette(value * 15, 15) : null;
  return (
    <select
      aria-label={label}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value))}
      style={{
        width: "100%", padding: "4px 1px", minHeight: 26,
        border: strong ? "2px solid #334155" : palette ? "none" : "1px solid #e2e8f0",
        borderRadius: 4, fontSize: 11,
        background: palette?.bg ?? "white",
        color: palette?.textColor ?? (strong ? "#64748b" : "#94a3b8"),
        fontWeight: palette ? 600 : 400,
      }}
    >
      <option value="">{emptyLabel}</option>
      {BREAK_OPTIONS.map((opt) => (
        <option key={opt.slots} value={opt.slots}>{opt.label}</option>
      ))}
    </select>
  );
}

export function BreakSizeSelects({ values, onChange, onReset, disabled }: BreakSizeSelectsProps) {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "flex-end" }}>
      {BREAK_LABELS.map((label, i) => (
        <div key={label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
            {label}
          </span>
          <BreakSelect label={label} value={values[i] ?? null} disabled={disabled} onChange={(v) => onChange(i, v)} />
        </div>
      ))}
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          title="Reset breaks to the defaults for this timing"
          style={{ background: "none", border: "1px solid #c7d2fe", borderRadius: 4, cursor: "pointer", color: "#6366f1", fontSize: 12, padding: "1px 5px", lineHeight: 1.4, marginBottom: 1 }}
        >↺</button>
      )}
    </div>
  );
}
